export interface HexagonPoint {
  x: number;
  y: number;
}

export interface HexagonGeometry {
  path: string;
  outline: HexagonPoint[];
}

export function generateHexagonGeometry(
  x: number,
  y: number,
  radius: number,
  gap: number = 6,
): HexagonGeometry {
  if (radius <= 0) return { path: `M ${x} ${y} Z`, outline: [{ x, y }] };
  const angleStep = (Math.PI * 2) / 6;
  const points = Array.from({ length: 6 }, (_, index) => ({
    x: x + radius * Math.cos(angleStep * index),
    y: y + radius * Math.sin(angleStep * index),
  }));
  let path = '';
  const outline: HexagonPoint[] = [];
  points.forEach((current, index) => {
    const next = points[(index + 1) % 6];
    const dx = next.x - current.x;
    const dy = next.y - current.y;
    const length = Math.sqrt(dx * dx + dy * dy);
    const gapPx = (gap / 100) * length;
    const startX = current.x + (dx / length) * gapPx;
    const startY = current.y + (dy / length) * gapPx;
    const endX = next.x - (dx / length) * gapPx;
    const endY = next.y - (dy / length) * gapPx;
    if (index === 0) path += `M ${startX} ${startY} `;
    path += `L ${endX} ${endY} `;
    const nextNext = points[(index + 2) % 6];
    const nextDx = nextNext.x - next.x;
    const nextDy = nextNext.y - next.y;
    const nextLength = Math.sqrt(nextDx * nextDx + nextDy * nextDy);
    const nextStartX = next.x + (nextDx / nextLength) * gapPx;
    const nextStartY = next.y + (nextDy / nextLength) * gapPx;
    path += `Q ${next.x} ${next.y}, ${nextStartX} ${nextStartY} `;
    for (let step = 0; step <= 6; step++) {
      const fraction = step / 6;
      const remaining = 1 - fraction;
      outline.push({
        x:
          remaining ** 2 * endX +
          2 * remaining * fraction * next.x +
          fraction ** 2 * nextStartX,
        y:
          remaining ** 2 * endY +
          2 * remaining * fraction * next.y +
          fraction ** 2 * nextStartY,
      });
    }
  });
  return { path: `${path}Z`, outline };
}

export function generateHexagonPath(
  x: number,
  y: number,
  radius: number,
  gap: number = 6,
): string {
  return generateHexagonGeometry(x, y, radius, gap).path;
}
